import React from 'react';

import { ArrowRightOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { PlanningLink } from 'components/Links';
import { ChangeDeltaWithArrow } from 'components/recipes/ChangeDeltaWithArrow';
import { Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';

import { formatNumber } from 'utils/formatting';
import { makePath, routePaths } from 'utils/routes';
import { asKeyOf } from 'utils/tables';

import { changeImpact, MinMaxChange } from './utils';

/** `5 → 8 ▲`, or just the new value when there is nothing to compare against. */
function BeforeAfter({ before, after }: { before: number | undefined; after: number }) {
  if (before === undefined) return <>{after}</>;
  if (before === after) {
    return (
      <span
        className={css`
          color: ${colors.neutral[500]};
        `}
      >
        {after}
      </span>
    );
  }
  return (
    <span
      className={css`
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        white-space: nowrap;
      `}
    >
      <span
        className={css`
          color: ${colors.neutral[500]};
        `}
      >
        {before}
      </span>
      <ArrowRightOutlined
        className={css`
          margin: 0 6px;
          font-size: 10px;
          color: ${colors.neutral[400]};
        `}
      />
      <span
        className={css`
          font-weight: 600;
        `}
      >
        {after}
      </span>
      {/* direction only: the before and after values already say how far the bound moved */}
      <ChangeDeltaWithArrow
        value={after - before}
        valueFormatter={() => ''}
        customArrowCss={css`
          margin-left: 4px;
          font-size: 12px;
        `}
      />
    </span>
  );
}

export function MinMaxChangesTable({
  changes,
  isLoading,
  pageSize = 20,
}: {
  changes: MinMaxChange[];
  isLoading?: boolean;
  pageSize?: number;
}) {
  const columns: ColumnType<MinMaxChange>[] = [
    {
      title: 'Item',
      dataIndex: asKeyOf<MinMaxChange>('itemId'),
      // opens Planning searched for this item and filtered to its location, as the rule detail page does
      render: (itemId: string, record) => (
        <PlanningLink
          to={makePath(routePaths.demandPlanning.planning, null, {
            query: itemId,
            where: { location: [record.locationId] },
          })}
        >
          {itemId}
        </PlanningLink>
      ),
    },
    {
      title: 'Location',
      dataIndex: asKeyOf<MinMaxChange>('locationId'),
    },
    {
      title: 'Min',
      dataIndex: asKeyOf<MinMaxChange>('newMin'),
      align: 'right',
      render: (_, record) => <BeforeAfter before={record.currentMin} after={record.newMin} />,
    },
    {
      title: 'Max',
      dataIndex: asKeyOf<MinMaxChange>('newMax'),
      align: 'right',
      render: (_, record) => <BeforeAfter before={record.currentMax} after={record.newMax} />,
    },
    {
      title: 'Impact',
      // keyed by column: the Max column already claims the newMax dataIndex
      key: 'impact',
      align: 'right',
      // net units this run added to or removed from the item-location
      render: (_, record) => {
        const impact = changeImpact(record);
        return (
          <div
            className={css`
              white-space: nowrap;
            `}
          >
            <div
              // weighted like the new min/max values, so the numbers that moved read alike
              className={css`
                color: ${impact === 0 ? colors.neutral[500] : colors.neutral[900]};
                font-weight: ${impact === 0 ? 400 : 600};
              `}
            >
              {impact > 0 ? `+${formatNumber(impact)}` : formatNumber(impact)}
            </div>
            <div
              className={css`
                font-size: 11px;
                color: ${colors.neutral[400]};
              `}
            >
              units
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<MinMaxChange>('exported'),
      width: 130,
      // a failed export means these values never reached the ERP, so the row must not read as exported
      render: (exported: boolean, record) =>
        exported ? (
          <span
            className={css`
              color: ${colors.success[600]};
              white-space: nowrap;
            `}
          >
            <CheckCircleOutlined /> Exported
          </span>
        ) : (
          <Tooltip title={record.error || 'The export failed, so this value was not sent to the ERP.'}>
            <span
              className={css`
                color: ${colors.danger[600]};
                white-space: nowrap;
              `}
            >
              <WarningOutlined /> Not exported
            </span>
          </Tooltip>
        ),
    },
  ];

  return <Table data={changes} columns={columns} isLoading={isLoading} rowKey="key" pageSize={pageSize} />;
}
